

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Skeleton,
    Snackbar,
    Alert,
    IconButton,
    Tooltip
} from "@mui/material";
import { Edit, Delete, Save, Close } from '@mui/icons-material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { useAuth } from 'auth/Auth';
import InlineEditField from 'components/CustonEditableInlineField/CustomEditableInlineField';
import { formatDate } from 'utils/DateTimeConversion';
import { commonIconButtonStyles, commonIconStyles } from 'assets/theme/components/iconButtonStyles';
import { UiDetailSection } from 'components/v2/record/DetailSection';

const TravelOrderItemDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { axiosInstance } = useAuth();
    const [item, setItem] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [updatedFields, setUpdatedFields] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    useEffect(() => {
        fetchItemData();
    }, [id]);

    const fetchItemData = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get(`/travel-order-items/${id}`);
            setItem(response.data);
        } catch (error) {
            console.error('Error fetching travel order item data:', error);
            setSnackbar({ open: true, message: 'Error loading travel order item details', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleFieldUpdate = (field, value) => {
        setUpdatedFields(prev => ({ ...prev, [field]: value }));
        setItem(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveChanges = async () => {
        if (Object.keys(updatedFields).length === 0) {
            setIsEditing(false);
            return;
        }
        try {
            await axiosInstance.put(`/travel-order-items/${id}`, updatedFields);
            setUpdatedFields({});
            setIsEditing(false);
            setSnackbar({ open: true, message: 'Travel Order Item updated successfully', severity: 'success' });
            fetchItemData();
        } catch (error) {
            console.error('Error updating travel order item:', error);
            setSnackbar({ open: true, message: 'Error updating travel order item', severity: 'error' });
        }
    };

    const handleDelete = async () => {
        if (window.confirm('Are you sure you want to delete this travel order item?')) {
            try {
                await axiosInstance.delete(`/travel-order-items/${id}`);
                setSnackbar({ open: true, message: 'Travel Order Item deleted successfully', severity: 'success' });
                setTimeout(() => navigate('/travel-order-items'), 1000);
            } catch (error) {
                console.error('Error deleting travel order item:', error);
                setSnackbar({ open: true, message: 'Error deleting travel order item', severity: 'error' });
            }
        }
    };

    const renderSection = (title, fields) => {
      // One body for both UIs: v2 shows it in a DetailSection, legacy keeps its card.
      const sectionBody = (
        <>
            <Grid container spacing={3}>
                {fields.map((field) => (
                    <Grid item xs={12} md={6} key={field.field}>
                        <InlineEditField
                            label={field.label}
                            value={item?.[field.field]}
                            options={field.options || null}
                            onSave={(newValue) => handleFieldUpdate(field.field, newValue)}
                            isEditing={isEditing}
                            readOnly={field.readOnly || false}
                            renderValue={field.renderValue || ((val) => (val ? String(val) : '-'))}
                        />
                    </Grid>
                ))}
            </Grid>
        </>
      );
      return (
        <UiDetailSection
          title={title}
          legacy={
            <MDBox
                mb={3}
                p={3}
                borderRadius="lg"
                sx={{
                    backgroundColor: '#ffffff',
                    boxShadow: 3
                }}
            >
                <MDTypography variant="h6" fontWeight="medium" mb={2}>{title}</MDTypography>
                {sectionBody}
            </MDBox>
          }
        >
          {sectionBody}
        </UiDetailSection>
      );
    };

    if (isLoading) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <Skeleton variant="rectangular" height={400} />
                </MDBox>
                
            </DashboardLayout>
        );
    }

    if (!item) {
        return (
            <DashboardLayout>
                <DashboardNavbar />
                <MDBox p={3}>
                    <MDTypography>Travel Order Item not found</MDTypography>
                </MDBox>
                
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDBox>
                                    <MDTypography variant="h5" color="white">
                                        {item.name}
                                    </MDTypography>
                                    <MDTypography variant="button" color="white" opacity={0.8}>
                                        Travel Order Item Details
                                    </MDTypography>
                                </MDBox>
                                <MDBox display="flex" gap={1}>
                                    {isEditing ? (
                                        <>
                                            <Tooltip title="Cancel">
                                                <IconButton
                                                    onClick={() => {
                                                        setIsEditing(false);
                                                        setUpdatedFields({});
                                                        fetchItemData();
                                                    }}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Close sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Save">
                                                <IconButton
                                                    onClick={handleSaveChanges}
                                                    sx={commonIconButtonStyles}
                                                >
                                                    <Save sx={commonIconStyles} />
                                                </IconButton>
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <Tooltip title="Edit Travel Order Item">
                                            <IconButton
                                                onClick={() => setIsEditing(true)}
                                                sx={commonIconButtonStyles}
                                            >
                                                <Edit sx={commonIconStyles} />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Delete Travel Order Item">
                                        <IconButton
                                            onClick={handleDelete}
                                            sx={commonIconButtonStyles}
                                        >
                                            <Delete sx={commonIconStyles} />
                                        </IconButton>
                                    </Tooltip>
                                </MDBox>
                            </MDBox>
                            <MDBox p={3}>
                                {renderSection("Item Information", [
                                    { label: "Item Name", field: "name" },
                                    { label: "Travel Order", field: "travel_order_id", readOnly: true, renderValue: (val) => item.travel_order?.name || val },
                                    { label: "Trip Logistics", field: "trip_component_id", readOnly: true, renderValue: (val) => item.trip_component?.name || val },
                                    { label: "Merchant Allocation", field: "merchant_allocation_id", readOnly: true, renderValue: (val) => item.merchant_allocation?.name || val || '-' },
                                    { label: "Merchant", field: "merchant_id", readOnly: true, renderValue: (val) => item.merchant?.name || val || '-' },
                                    { label: "Pax Count", field: "pax_count" },
                                    { label: "Unit Price", field: "unit_price", renderValue: (val) => val ? `$${parseFloat(val).toFixed(2)}` : '-' },
                                ])}

                                {renderSection("Service Information", [
                                    { label: "Service Start", field: "service_start", readOnly: true, renderValue: (val) => val ? formatDate(val, 'datetime') : '-' },
                                    { label: "Service End", field: "service_end", readOnly: true, renderValue: (val) => val ? formatDate(val, 'datetime') : '-' },
                                    { label: "Fulfillment Status", field: "fulfillment_status", options: [
                                        { value: "Pending", label: "Pending" },
                                        { value: "Allocated", label: "Allocated" },
                                        { value: "Confirmed", label: "Confirmed" },
                                        { value: "Waitlisted", label: "Waitlisted" },
                                        { value: "Cancelled", label: "Cancelled" },
                                        { value: "Split", label: "Split" }
                                    ]},
                                    { label: "Merchant Confirmation No", field: "merchant_confirmation_no" },
                                ])}

                                {renderSection("Merchant Status", [
                                    { label: "Merchant Acknowledgement", field: "merchant_acknowledgement", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }], renderValue: (val) => (val ? "Yes" : "No") },
                                    { label: "Order Sent to Merchant", field: "merchant_order_sent", options: [{ value: true, label: "Yes" }, { value: false, label: "No" }], renderValue: (val) => (val ? "Yes" : "No") },
                                ])}

                                {renderSection("Audit Information", [
                                    { label: "Created By", field: "created_by_name", readOnly: true },
                                    { label: "Modified By", field: "updated_by_name", readOnly: true },
                                    { label: "Created Date", field: "created_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                    { label: "Modified Date", field: "updated_at", readOnly: true, renderValue: (val) => formatDate(val, 'datetime') },
                                ])}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
            
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default TravelOrderItemDetail;

